import { ErrorIcon, Loader, Success, TextBlock, Warning } from "@bcc-ui/ui-components";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { AssetImportJob, AssetImportJobStatus } from "services/asset-management/AssetImportJobService";

export const DIVIDER_DOT = ` • `;

export interface BadgeTextProps {
    primary: ReactNode;
    secondary: ReactNode;
}

export const BadgeText = (props: BadgeTextProps) => (
    <>
        <TextBlock disableBottomSpacing>{props.primary}</TextBlock>
        {props.secondary && (
            <>
                <TextBlock disableBottomSpacing>{DIVIDER_DOT}</TextBlock>
                <TextBlock disableBottomSpacing>{props.secondary}</TextBlock>
            </>
        )}
    </>
);

export const getJobStatusBackgroundColor = (status: AssetImportJobStatus): string => {
    switch (status) {
        case AssetImportJobStatus.SUCCEEDED:
            return "var(--app-jobStatus-badge-succeed-iconColor)";

        case AssetImportJobStatus.FAILED:
            return "var(--app-jobStatus-badge-failed-iconColor)";

        case AssetImportJobStatus.WARNING:
            return "var(--app-jobStatus-badge-warning-iconColor)";

        case AssetImportJobStatus.STARTED:
            return "var(--app-dataBearing-badge-backgroundColor)";
    }
};

export const getJobStatusIcon = (status: AssetImportJobStatus): ReactNode => {
    switch (status) {
        case AssetImportJobStatus.SUCCEEDED:
            return <Success size={20} />;

        case AssetImportJobStatus.FAILED:
            return <ErrorIcon size={20} />;

        case AssetImportJobStatus.WARNING:
            return <Warning size={20} />;

        case AssetImportJobStatus.STARTED:
            return <Loader small />;
    }
};

export const jobStatusTriggerText = (job: AssetImportJob, jobCount: number) => {
    const { t } = useTranslation();
    const singleJob = jobCount === 1;
    switch (job.status) {
        case AssetImportJobStatus.STARTED:
            return (
                <BadgeText
                    primary={t("AssetManagement.overview.totalAssetsCard.importing", { count: jobCount })}
                    secondary={
                        singleJob
                            ? t("AssetManagement.overview.totalAssetsCard.jobAssetsProgress", {
                                  added: job.totalImportedAssets,
                                  count: job.totalRequestedAssets,
                              })
                            : undefined
                    }
                />
            );

        case AssetImportJobStatus.WARNING:
            return (
                <BadgeText
                    primary={
                        singleJob
                            ? t("AssetManagement.overview.totalAssetsCard.assetsAdded", {
                                  added: job.totalImportedAssets,
                                  count: job.totalRequestedAssets,
                              })
                            : t("AssetManagement.overview.totalAssetsCard.importsNeedReview", { count: jobCount })
                    }
                    secondary={
                        singleJob
                            ? t("AssetManagement.overview.totalAssetsCard.assetsQueued", {
                                  count: job.totalFailedAssets,
                              })
                            : undefined
                    }
                />
            );

        case AssetImportJobStatus.FAILED:
            return (
                <BadgeText
                    primary={
                        singleJob
                            ? t("AssetManagement.overview.totalAssetsCard.import_failed")
                            : t("AssetManagement.overview.totalAssetsCard.importsFailed", { count: jobCount })
                    }
                    secondary={
                        singleJob
                            ? t("AssetManagement.overview.totalAssetsCard.assetsAdded", {
                                  added: 0,
                                  count: job.totalRequestedAssets,
                              })
                            : undefined
                    }
                />
            );

        case AssetImportJobStatus.SUCCEEDED:
            return (
                <BadgeText
                    primary={t("AssetManagement.overview.totalAssetsCard.import_complete", { count: jobCount })}
                    secondary={
                        singleJob
                            ? t("AssetManagement.overview.totalAssetsCard.assetsAddedTotal", {
                                  count: job.totalImportedAssets,
                              })
                            : undefined
                    }
                />
            );
    }
};
